import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useCustomerContext } from '@unlikelystudio/react-ecommerce-hooks'
import { InlineCtaProps } from '~/components/UI/InlineCta'
import { serializeWishlistProductCard } from '~/data/serialize-wishlist-products'

import useAlerts from "~/hooks/useAlerts"
import useLocale from '~/hooks/useLocale'
import { useWishlistQueryKey, type WishlistResponse } from "~/hooks/wishlist/useGetWishlist"
import { useWishlistCookie } from "~/hooks/wishlist/useWishlistCookie"
import { createWishlist, updateWishlistProducts } from '~/lib/handle-api'


import type {
  CreateWishlistRequestBody,
  CreateWishlistRequestPayload,
  UpdatedProductPayload,
  UpdatedProductRequestPayload,
  UpdateWishlistProductsRequestBody
} from "~/lib/swym/wishlist/types"


export type UpdateWishlistInput = {
  variantId: number
  productId: number
  link: InlineCtaProps
  quantity?: number
  uuid: string
}

export type WishlistUpdateResponse = {
  products: UpdatedProductPayload[]
  success?: boolean
}

//TODO: update any type
export function useAddProductToWishlist({ onSuccess }: { onSuccess?: () => void }) {
  const { wishlistCookie, createWishlistCookie } = useWishlistCookie()
  const wishlistQueryKey = useWishlistQueryKey()
  const queryClient = useQueryClient()
  const locale = useLocale()
  const triggerSuccessAlert = useAlerts()
  const triggerErrorAlert = useAlerts()
  const { customerAccessToken } = useCustomerContext()

  return useMutation<WishlistUpdateResponse, unknown, UpdateWishlistInput>(
    async (params) => {
      const requestPayload: UpdateWishlistProductsRequestBody = {
        wishlist: wishlistCookie,
        action: "add",
        products: [
          {
            epi: params.variantId,
            empi: params.productId,
            du: `${process.env.NEXT_PUBLIC_VERCEL_URL}${params.link.href}`,
            quantity: params.quantity ?? 1,
            uuid: params.uuid,
          },
        ],
      }

      // If the wishlist doesn't have a list ID, create a new wishlist
      if (!wishlistCookie?.lid) {
        const payload: CreateWishlistRequestBody = {
          regid: wishlistCookie?.regid,
          sessionid: wishlistCookie?.sessionid,
          lname: "My Wishlist",
        }

        const createWishlistResponse: CreateWishlistRequestPayload = await createWishlist({
          accessToken: customerAccessToken,
          payload,
        })

        const litsId = createWishlistResponse?.lid

        createWishlistCookie({ ...wishlistCookie, lid: litsId })

        if (litsId) {
          requestPayload.wishlist = {
            ...wishlistCookie,
            lid: litsId,
          }
        }
      }

      const response: UpdatedProductRequestPayload = await updateWishlistProducts({
        accessToken: customerAccessToken,
        payload: requestPayload,
      })

      return {
        products: response?.a,
        ...response,
      }

    },
    {
      // Optimistically update the wishlist
      onMutate: async ({ variantId, productId, uuid }) => {
        await queryClient.cancelQueries(wishlistQueryKey)

        const prevResponse = queryClient.getQueryData<WishlistResponse>(wishlistQueryKey)

        if (prevResponse) {
          const newProduct = await serializeWishlistProductCard({
            locale,
            productField: {
              epi: variantId,
              empi: productId,
              cprops: {
                uuid: uuid,
              }
            },
          })

          queryClient.setQueryData(wishlistQueryKey, { ...prevResponse, products: [...(prevResponse.products ?? []), newProduct] })
        }

        return { prevResponse }
      },
      onSuccess: (data) => {
        if (data?.products?.[0]?.hasError || data?.success === false) {
          triggerErrorAlert()
          return
        }

        onSuccess?.()
        triggerSuccessAlert("ADDED_WISHLIST")
      },
      onError: (_err, _variables, context: { prevResponse?: WishlistResponse }) => {
        // If the mutation fails, use the previous value as a backup
        if (context?.prevResponse) {
          queryClient.setQueryData(wishlistQueryKey, context.prevResponse)
        }

        triggerErrorAlert()
      },
      onSettled: () => {
        // Invalidate and refetch the wishlist
        queryClient.invalidateQueries([wishlistQueryKey[0]], { exact: false })
      },
    }
  )
}
